/**
 * tickContractsLifecycle — pure helper extracted from coreSlice.nextDay
 * (03/06/2026 round 3 refactor of the contracts block).
 *
 * Drives the daily lifecycle of each active contract :
 *   • Decrement `daysRemaining` on working days.
 *   • Track `daysServed` (working days with the resource available) and
 *     `missedDeliveries` (working days where the assigned vehicle was
 *     broken/maintained or the driver sick/striking/on vacation/training).
 *   • Drop contracts whose `daysRemaining` reached 0.
 *   • Emit notifications + CRM event descriptors for the orchestrator to
 *     dispatch (helper stays pure, no `get()` access).
 *
 * @param {Object} state
 * @param {boolean} working - true on working days
 * @param {Object<string, true>} contractMisses - map of contract instanceIds
 *   that missed delivery this tick (populated by computeDailyRevenue)
 * @returns {{
 *   updatedContracts: Array,
 *   completedContractIds: Array<string>,
 *   notifications: Array,
 *   crmEvents: Array<{clientId: string, eventType: string, payload: object}>
 * }}
 */
import { generateId } from '../helpers';

export function tickContractsLifecycle(state, working, contractMisses = {}) {
  const notifications = [];
  const crmEvents = [];

  // Step 1 — Decrement / track per-contract counters.
  const updatedContracts = state.activeContracts.map(c => {
    const missedToday = working && contractMisses[c.instanceId];
    return {
      ...c,
      daysRemaining: working ? c.daysRemaining - 1 : c.daysRemaining,
      daysServed: (working && !missedToday) ? (c.daysServed || 0) + 1 : (c.daysServed || 0),
      missedDeliveries: missedToday ? (c.missedDeliveries || 0) + 1 : (c.missedDeliveries || 0),
    };
  }).filter(c => c.daysRemaining > 0);

  const completedContractIds = working
    ? state.activeContracts.filter(c => c.daysRemaining <= 1).map(c => c.instanceId)
    : [];
  // 04/02/2026 — Phase Q1 (retour user) : missions PONCTUELLES → 1 jour de
  // retour (chauffeur et camion indispo le lendemain). On collecte les
  // contrats ponctuels qui se terminent pour que coreSlice/tickVehicles
  // applique le délai de retour. Seuil distance > 300 km demandé par user ;
  // tous les contrats ponctuels du jeu sont des missions long-courrier
  // (cf. spotEngagementSlice + AO messagerie), donc on applique sans gate.
  const completedPunctualIds = working
    ? state.activeContracts
        .filter(c => c.daysRemaining <= 1 && c.frequency === 'ponctuel')
        .map(c => c.instanceId)
    : [];

  if (!working) {
    return { updatedContracts, completedContractIds, completedPunctualIds, notifications, crmEvents };
  }

  // (a) End of contract → +2 loyalty + notification.
  state.activeContracts
    .filter(c => c.daysRemaining <= 1)
    .forEach(c => {
      notifications.push({
        id: generateId(),
        type: 'contract_complete',
        title: 'Contrat terminé',
        message: `Le contrat "${c.name}" est arrivé à son terme. Véhicule et chauffeur libérés.`,
        day: state.totalDays + 1,
        read: false,
      });
      if (c.clientId) {
        crmEvents.push({
          clientId: c.clientId,
          eventType: 'contract_completed_ontime',
          payload: { contractId: c.instanceId, contractName: c.name },
        });
      }
    });

  // (b) Mid-flight weekly tick — every 7 served days fires +1 loyalty.
  updatedContracts.forEach(c => {
    const served = c.daysServed || 0;
    if (served > 0 && served % 7 === 0 && c.clientId) {
      crmEvents.push({
        clientId: c.clientId,
        eventType: 'delivery_completed_ontime',
        payload: { contractId: c.instanceId, contractName: c.name, day: state.totalDays + 1 },
      });
    }
  });

  // (c) Missed delivery — notify + on each multiple of 3, push CRM late event.
  updatedContracts.forEach(c => {
    if (!contractMisses[c.instanceId]) return;
    const m = c.missedDeliveries || 0;
    notifications.push({
      id: generateId(),
      type: 'contract_missed',
      title: 'Livraison manquée',
      message: `Contrat "${c.name}" : ressource indisponible. ${m} jour(s) manqué(s) au total.`,
      day: state.totalDays + 1,
      read: false,
    });
    if (m > 0 && m % 3 === 0 && c.clientId) {
      crmEvents.push({
        clientId: c.clientId,
        eventType: 'contract_completed_late',
        payload: { contractId: c.instanceId, contractName: c.name, missedDays: m },
      });
    }
  });

  return { updatedContracts, completedContractIds, completedPunctualIds, notifications, crmEvents };
}
